import React, { useState, useContext, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { TCOData } from '../utils/TCOData';
import { HomeData, InputValue, initialValue } from '../utils/data';
import { DisplaySymbol } from '../utils/currency';
import TooltipSlider from './slider/TooltipSlider';
import { DoughnutChart } from './charts/doughnutChart';
import {
  formatNumberWithAbbreviations,
  formatNumberWithAbbreviationsLetters,
} from '../utils/Formatters';
import AdministrativeBarChart from './charts/administrativeChart';
import TCOBarChart from './charts/tcoChart';
import { AdministrativeCostsData } from '../utils/AdministrativeCostsData';
import Button from './Button';
import './style.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.js';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import * as URX_FORM from '../constant/urx-form';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { FormModal } from 'src/common/utils/FormModal';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import Cookies from 'js-cookie';
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactGA from 'react-ga4';
const TRACKING_ID = 'G-RHMWQ4WS0C'; // your Measurement ID
import messages from '../locales/messages';
import { Modal, Tooltip } from 'carbon-components-react';
import { shortcode } from '../ESGIBMFlashsystems.widget';

const ESGIBMFlashsystems = () => {
  const [inputValue, setInputValue] = useState<InputValue>(initialValue);
  const [assumptionTotal, setAssumptionTotal] = useState(100);
  const [showAssumption, setShowAssumption] = useState(false);
  const [showAdministrativeChart, setShowAdministrativeChart] = useState(false);
  const [environment] = useEnv();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const cookieURL = useCookiesURL(shortcode);
  const intl = useIntl();

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const initialState = {
    error: false,
    isLoaded: false,
    max: 100,
    ticket: inputValue?.tickets ?? 10,
    tcoData: new TCOData(),
    homeData: new HomeData(),
    administrativeCostsData: new AdministrativeCostsData(),
  };

  const [state, setState] = useState(initialState);

  const handleManagementFunctions = (
    value: number | number[],
    slider: string,
  ): void => {
    if (typeof value === 'number') {
      const [val, sum, max] = state.homeData.FTETimeSpentValuesSum(
        value,
        slider,
      );
      setAssumptionTotal(sum);
      setInputValue({ ...inputValue, [slider]: val });
    }
  };

  const handleSelectFunctions = (e: any): void => {
    console.log('handle select');
    const { value, selectedIndex } = e.target;

    const optionElement = e.target.childNodes[selectedIndex];
    const numberValue = optionElement.getAttribute('data-id');

    setInputValue({
      ...inputValue,
      maturity: value,
      maturityValue: numberValue,
    });
  };

  const getValueState = (): boolean => {
    return (
      inputValue?.maturity === intl.formatMessage(messages.select_one) ||
      inputValue?.nbrAdmins === undefined ||
      inputValue?.usableCapacity === undefined
    );
  };

  const resetDefault = () => {
    state.homeData.setDefault();
    setInputValue({
      ...inputValue,
      salary: 163200,
      pctFTEtime: 50,
      tickets: 10,
      timeTickets: 90,
      pct_planningEval: 20,
      pct_monitoring: 15,
      pct_applicationOptimization: 20,
      pct_securityEomp: 10,
      pct_userAccess: 10,
      pct_dataProtection: 15,
      pct_newInstalls: 10,
    });
    setAssumptionTotal(100);
    setState({ ...state, ticket: 10 });
  };

  const redirectToURXForm = () => {
    window.location.href =
      'https://www.ibm.com/account/reg/signup?formid=urx-52690';
  };

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_DYNAMIC_Q_1: inputValue.nbrAdmins.toString(),
          Q_DYNAMIC_Q_2: inputValue.usableCapacity.toString(),
          Q_DYNAMIC_Q_3: inputValue?.maturity.toString(),
        },
      }),
    );
  };

  const handleGaClick = () => {
    ReactGA.event({
      category: 'calculate',
      action: 'Click',
      label: 'calc',
    });
  };

  useEffect(() => {
    setState({ ...state, ticket: inputValue.tickets });

    state.homeData.Init();
    const administrativeCostsData = state.administrativeCostsData;
    administrativeCostsData.Calculate(inputValue);

    const tcoData = state.tcoData;
    tcoData.Calculate(inputValue, state.administrativeCostsData);

    setState({
      ...state,
      administrativeCostsData: state.administrativeCostsData,
      tcoData: state.tcoData,
    });
  }, []);

  useEffect(() => {
    const administrativeCostsData = state.administrativeCostsData;
    administrativeCostsData.Calculate(inputValue);

    const tcoData = state.tcoData;
    tcoData.Calculate(inputValue, state.administrativeCostsData);

    ReactGA.initialize(TRACKING_ID);
    // Send pageview with a custom path
    ReactGA.send({
      hitType: 'pageview',
      page: '/landingpage',
      title: 'Landing Page',
    });

    setState({
      ...state,
      administrativeCostsData: state.administrativeCostsData,
      tcoData: state.tcoData,
    });
    console.log(inputValue);
  }, [inputValue]);

  return (
    <div id="esg-ibm-fs" className="widget-styles">
      <Helmet>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/loader.js`}
        ></script>
        <script
          type="application/javascript"
          src={`https://www.ibm.com/account/ibmidutil/widget/js/main.js`}
        ></script>
      </Helmet>
      <div className="esg-main widget-styles">
        <div className="homepage-content container">
          <div id="ESG-IBM-form" className="homepage-form">
            <div className="title">
              <h2>
                <FormattedMessage {...messages.how_much_save} />
                <span>
                  {' '}
                  <FormattedMessage {...messages.ibm_flashsystem} />
                </span>
              </h2>
            </div>
            <div className="form-box">
              <div className="grey-box">
                <div className="question-row">
                  <div className="question-box">
                    <p>
                      <div id="numberOfadmin">
                        <FormattedMessage {...messages.number_of_admins} />
                      </div>
                    </p>
                    <div className="input-field">
                      <NumericFormat
                        aria-labelledby="numberOfadmin"
                        required
                        name="nbrAdmins"
                        type="text"
                        value={inputValue?.nbrAdmins}
                        thousandSeparator={true}
                        onValueChange={(e) => {
                          setInputValue({
                            ...inputValue,
                            nbrAdmins: e.floatValue ?? 0,
                          });
                        }}
                      />
                      <div className="input-info">
                        <Tooltip id="ttnbrAdmins">
                          <p>
                            <FormattedMessage {...messages.number_of_admins} />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="question-row">
                  <div className="question-box">
                    <p>
                      <div id="usageCapacity">
                        <FormattedMessage {...messages.usable_capacity} />
                      </div>
                    </p>
                    <div className="input-field">
                      <NumericFormat
                        required
                        aria-labelledby="usageCapacity"
                        name="usableCapacity"
                        type="text"
                        decimalScale={2}
                        value={inputValue?.usableCapacity}
                        thousandSeparator={true}
                        onValueChange={(e) => {
                          setInputValue({
                            ...inputValue,
                            usableCapacity: e.floatValue ?? 0,
                          });
                        }}
                      />
                      <div className="input-info">
                        <Tooltip id="ttUsageCapacity">
                          <p>
                            <FormattedMessage {...messages.amount_of_storage} />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="question-row">
                  <div className="question-box">
                    <p>
                      <div id="modernSystem">
                        <FormattedMessage {...messages.how_modern_system} />
                      </div>
                    </p>
                    <div className="input-field">
                      <select
                        aria-labelledby="modernSystem"
                        value={inputValue?.maturity}
                        onChange={handleSelectFunctions}
                        name="maturity"
                      >
                        {state.homeData.MaturityType.map((type) => (
                          <option
                            key={type.id}
                            value={type.text}
                            data-id={type.number}
                          >
                            {type.text}
                          </option>
                        ))}
                      </select>
                      <div className="input-info">
                        <Tooltip id="ttModernSystem">
                          <p>
                            <FormattedMessage
                              {...messages.how_modern_system_tooltip1}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.how_modern_system_tooltip2}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.how_modern_system_tooltip3}
                            />
                            <br />
                            <FormattedMessage
                              {...messages.how_modern_system_tooltip4}
                            />
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                handleClick={() => setShowAssumption(true)}
                title={intl.formatMessage(messages.view_modify_assumption)}
                className="outline home"
              />
              {!inputValue.openResult && (
                <Button
                  disable={getValueState()}
                  handleClick={() => {
                    handleGaClick;
                    window.open(
                      'https://www.ibm.com/account/reg/us-en/signup?formid=MAIL-storageflash',
                      '_blank',
                    );
                  }}
                  title={intl.formatMessage(messages.see_your_results)}
                  className="home"
                />
              )}
            </div>
            <Modal
              open={showAssumption}
              size={'md'}
              onRequestClose={() => setShowAssumption(false)}
              className="result-form"
              passiveModal={true}
              preventCloseOnClickOutside={true}
            >
              <div id="assumptionModal">
                <header className="title assumption">
                  <h2>
                    <FormattedMessage {...messages.view_modify_assumption} />
                  </h2>
                </header>
                <div className="form-box">
                  <div className="grey-box">
                    <div className="question-row">
                      <div className="question-box">
                        <p>
                          <FormattedMessage {...messages.system_admin_salary} />
                        </p>
                        <div className="input-field">
                          <NumericFormat
                            required
                            name="salary"
                            type="text"
                            value={inputValue?.salary}
                            decimalScale={2}
                            prefix={DisplaySymbol(inputValue?.currency)}
                            thousandSeparator={true}
                            onValueChange={(e) => {
                              setInputValue({
                                ...inputValue,
                                salary: e.floatValue ?? 0,
                              });
                            }}
                          />
                          <div className="input-info">
                            <Tooltip id="ttsalary">
                              <p>
                                <FormattedMessage
                                  {...messages.system_admin_salary}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="question-row">
                      <div className="question-box">
                        <p>
                          <FormattedMessage {...messages.percentage_of_fte} />
                        </p>
                        <div className="input-field">
                          <NumericFormat
                            required
                            name="pctFTEtime"
                            type="text"
                            suffix={'%'}
                            decimalScale={2}
                            value={inputValue?.pctFTEtime}
                            thousandSeparator={true}
                            onValueChange={(e) => {
                              setInputValue({
                                ...inputValue,
                                pctFTEtime: e.floatValue ?? 0,
                              });
                            }}
                          />
                          <div className="input-info">
                            <Tooltip id="ttpctFTEtime">
                              <p>
                                <FormattedMessage
                                  {...messages.percentage_of_fte_toolip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="question-row">
                      <div className="question-box">
                        <p>
                          <FormattedMessage
                            {...messages.average_number_storage}
                          />
                        </p>
                        <div className="input-field">
                          <input
                            required
                            type="number"
                            name="tickets"
                            min={1}
                            max={100}
                            value={state.ticket}
                            onChange={(e) => {
                              const value = Math.max(
                                1,
                                Math.min(100, Number(e.target.value)),
                              );
                              setState({ ...state, ticket: value });
                              setInputValue({
                                ...inputValue,
                                tickets: value,
                              });
                            }}
                          />
                          <div className="input-info">
                            <Tooltip id="tttickets">
                              <p>
                                <FormattedMessage
                                  {...messages.average_number_storage_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="question-row">
                      <div className="question-box">
                        <p>
                          <FormattedMessage
                            {...messages.average_time_to_close_ticket}
                          />
                        </p>
                        <div className="input-field">
                          <NumericFormat
                            required
                            type="text"
                            name="timeTickets"
                            decimalScale={2}
                            value={inputValue?.timeTickets}
                            thousandSeparator={true}
                            onValueChange={(e) => {
                              setInputValue({
                                ...inputValue,
                                timeTickets: e.floatValue ?? 0,
                              });
                            }}
                          />
                          <div className="input-info">
                            <Tooltip id="tttimeTickets">
                              <p>
                                <FormattedMessage
                                  {...messages.average_time_to_close_ticket_tooltip}
                                />
                              </p>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grey-box third">
                    <p>
                      <FormattedMessage {...messages.percentage_fte_time} />
                      <span className="input-info">
                        <Tooltip id="ttpercentageFteTime">
                          <p>
                            <FormattedMessage
                              {...messages.percentage_fte_time_tooltip}
                            />
                          </p>
                        </Tooltip>
                      </span>
                    </p>
                    <div className="slider-boxes">
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage
                                {...messages.planning_evaluations}
                              />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_planningEval',
                                );
                              }}
                              value={inputValue?.pct_planningEval}
                              defaultValue={20}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.monitoring_env} />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_monitoring',
                                );
                              }}
                              value={inputValue?.pct_monitoring}
                              defaultValue={15}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.application_opt} />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_applicationOptimization',
                                );
                              }}
                              value={inputValue?.pct_applicationOptimization}
                              defaultValue={20}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage
                                {...messages.security_compliance}
                              />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_securityEomp',
                                );
                              }}
                              value={inputValue?.pct_securityEomp}
                              defaultValue={10}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage
                                {...messages.percentage_fte_time_tooltip}
                              />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_userAccess',
                                );
                              }}
                              value={inputValue?.pct_userAccess}
                              defaultValue={10}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage {...messages.data_protection} />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_dataProtection',
                                );
                              }}
                              value={inputValue?.pct_dataProtection}
                              defaultValue={15}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box last">
                        <div className="question-row">
                          <div className="question-box">
                            <p>
                              <FormattedMessage
                                {...messages.new_install_apps}
                              />
                            </p>
                          </div>
                        </div>
                        <div className="question-row">
                          <div className="range-slider with-four-labels">
                            <TooltipSlider
                              min={0}
                              max={state.max}
                              isShowTooltip={false}
                              onChange={(val) => {
                                handleManagementFunctions(
                                  val,
                                  'pct_newInstalls',
                                );
                              }}
                              value={inputValue?.pct_newInstalls}
                              defaultValue={10}
                              step={1}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="slider-box">
                        <p className="total">
                          <span>
                            {' '}
                            <FormattedMessage {...messages.total} />{' '}
                          </span>
                          <span
                            className={
                              assumptionTotal === 100 ? 'green' : 'red'
                            }
                          >
                            <NumericFormat required displayType="text" />
                            {`${assumptionTotal}%`}
                          </span>
                          <span className="notes">
                            <span>
                              {' '}
                              <FormattedMessage {...messages.total_tooltip} />
                            </span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <DoughnutChart
                      expectedAnnualCost={
                        state.administrativeCostsData.expectedAnnualCost
                      }
                    />
                  </div>
                  <div>
                    <div className="graph-labels circle">
                      <div className="graph-label">
                        <div className="first-label"></div>
                        <p>
                          <FormattedMessage
                            {...messages.planning_evaluations}
                          />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="second-label"></div>
                        <p>
                          <FormattedMessage {...messages.monitoring_env} />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="third-label"></div>
                        <p>
                          <FormattedMessage {...messages.application_opt} />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="fourth-label"></div>
                        <p>
                          <FormattedMessage {...messages.security_compliance} />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="fifth-label"></div>
                        <p>
                          <FormattedMessage {...messages.user_access} />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="sixth-label"></div>
                        <p>
                          <FormattedMessage {...messages.data_protection} />
                        </p>
                      </div>
                      <div className="graph-label">
                        <div className="seventh-label"></div>
                        <p>
                          <FormattedMessage {...messages.new_install_apps} />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="graph-btn desktop">
                      <Button
                        className={assumptionTotal === 100 ? '' : 'disable'}
                        handleClick={() => {
                          setShowAssumption(false);
                        }}
                        title={intl.formatMessage(messages.accept_assumption)}
                        disable={assumptionTotal !== 100}
                      />
                      <Button
                        handleClick={resetDefault}
                        title={intl.formatMessage(messages.reset_default)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
          <div className="result-section open">
            <div className="result-label">
              <p className="details">
                <span className="blue">
                  <NumericFormat
                    required
                    displayType="text"
                    value={formatNumberWithAbbreviations(
                      state.tcoData.TCOSavings,
                    )}
                    prefix={DisplaySymbol(inputValue?.currency)}
                    thousandSeparator={true}
                  />
                  {formatNumberWithAbbreviationsLetters(
                    state.tcoData.TCOSavings,
                  )}
                </span>{' '}
                <FormattedMessage {...messages.annual_storage_savings} />
              </p>
            </div>
            <div
              className={`result-section ${
                inputValue.openResult ? 'open' : 'open'
              }`}
            >
              <div className="result-label">
                <p className="details">
                  <span className="blue">
                    <NumericFormat
                      required
                      displayType="text"
                      value={formatNumberWithAbbreviations(
                        state.administrativeCostsData.totalAdminSavings,
                      )}
                      prefix={DisplaySymbol(inputValue?.currency)}
                      thousandSeparator={true}
                    />
                    {formatNumberWithAbbreviationsLetters(
                      state.administrativeCostsData.totalAdminSavings,
                    )}
                  </span>{' '}
                  <FormattedMessage
                    {...messages.annual_administrative_savings_benefits}
                  />
                </p>
              </div>
              {showAdministrativeChart ? (
                <div className="graph-container">
                  <AdministrativeBarChart
                    expectedAnnualCost={
                      state.administrativeCostsData.expectedAnnualCost
                    }
                    expectedAnnualCostIBM={
                      state.administrativeCostsData.expectedAnnualCostIBM
                    }
                    pctTCO={state.tcoData.savingsValue.pctTCO}
                    inputValue={inputValue}
                  />
                </div>
              ) : (
                <div className="graph-container">
                  <TCOBarChart
                    expectedTCOCost={state.tcoData.expectedTCOCost}
                    expectedTCOCostIBM={state.tcoData.expectedTCOCostIBM}
                    pctTCO={state.tcoData.savingsValue.pctTCO}
                    inputValue={inputValue}
                  />
                </div>
              )}
              <div className="result-description">
                <div className="graphToggle">
                  <p className="toggleTitle">
                    <div id="toggelChartObject">
                      <FormattedMessage {...messages.toggle_chart} />
                    </div>
                  </p>
                  <select
                    aria-labelledby="toggelChartObject"
                    defaultValue={0}
                    onChange={(e) => {
                      setShowAdministrativeChart(
                        e.target.value === 'Administrative',
                      );
                    }}
                    name="toggleChart"
                    className="selectToggle"
                  >
                    <option value="TCO">
                      {intl.formatMessage(messages.storage_tco)}
                    </option>
                    <option value="Administrative">
                      {intl.formatMessage(messages.administrative_cost)}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <FormModal
            pageTitle={intl.formatMessage(messages.esgibm_flashsystems)}
            object={intl.formatMessage(messages.esgibm_flashsystems)}
            productTitle={intl.formatMessage(messages.esgibm_flashsystems)}
            pageUrl={''}
            location={'Flashsystems'}
            bpName={''}
            logo={''}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
          ></FormModal>
        </div>
      </div>
    </div>
  );
};

export default ESGIBMFlashsystems;
